import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import { getStatusChip } from "./ReservationList";
import type { ReservationDetail as ReservationDetailType } from "./types";
import styles from "./ReservationDetail.module.scss";

interface ReservationDetailProps {
  open: boolean;
  reservation: ReservationDetailType | null;
  isLoading?: boolean;
  canApprove: boolean;
  onClose: () => void;
  onApprove: (id: number, reason?: string) => Promise<void>;
  onReject: (id: number, reason?: string) => Promise<void>;
}

export function ReservationDetail({
  open,
  reservation,
  isLoading = false,
  canApprove,
  onClose,
  onApprove,
  onReject,
}: ReservationDetailProps) {
  const [showApproveInput, setShowApproveInput] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!reservation && !isLoading) return null;

  const isPending = reservation?.status === "pending";
  const showActions = canApprove && isPending;

  const handleApprove = async () => {
    if (!reservation) return;
    setIsSubmitting(true);
    try {
      await onApprove(reservation.id, approveReason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reservation) return;
    setIsSubmitting(true);
    try {
      await onReject(reservation.id, rejectReason.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Reserva #{reservation?.id} — {reservation ? getStatusChip(reservation.status) : null}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress />
          </div>
        ) : reservation ? (
          <>
            {/* Header info */}
            <div className={styles.headerInfo}>
              {reservation.quote_number && (
                <Typography variant="body2" color="textSecondary">
                  Cotización: {reservation.quote_number}
                </Typography>
              )}
              {reservation.notes && (
                <Typography variant="body2" color="textSecondary">
                  Notas: {reservation.notes}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Creada: {new Date(reservation.created_at).toLocaleString("es-CO")}
              </Typography>
              {reservation.approved_at && (
                <Typography variant="body2" color="textSecondary">
                  Aprobada:{" "}
                  {new Date(reservation.approved_at).toLocaleString("es-CO")}
                </Typography>
              )}
              {reservation.rejected_at && (
                <Typography variant="body2" color="textSecondary">
                  Rechazada:{" "}
                  {new Date(reservation.rejected_at).toLocaleString("es-CO")}
                  {reservation.rejection_reason &&
                    ` — ${reservation.rejection_reason}`}
                </Typography>
              )}
            </div>

            {/* Items table */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell>Dirección</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Stock disponible</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservation.items?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.product_name}</TableCell>
                    <TableCell>{item.address_name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{item.available_stock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}

        {/* Approve reason input */}
        {showApproveInput && (
          <TextField
            label="Motivo de aprobación (opcional)"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={approveReason}
            onChange={(e) => setApproveReason(e.target.value)}
            inputProps={{ "aria-label": "motivo-aprobacion" }}
          />
        )}

        {/* Reject reason input */}
        {showRejectInput && (
          <TextField
            label="Motivo de rechazo"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            inputProps={{ "aria-label": "motivo-rechazo" }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cerrar
        </Button>

        {showActions && !showRejectInput && !showApproveInput && (
          <>
            <Button
              onClick={() => setShowApproveInput(true)}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={<CheckCircleIcon />}
            >
              Aprobar
            </Button>
            <Button
              onClick={() => setShowRejectInput(true)}
              color="secondary"
              variant="outlined"
              disabled={isSubmitting}
              startIcon={<CancelIcon />}
            >
              Rechazar
            </Button>
          </>
        )}

        {showApproveInput && (
          <>
            <Button
              onClick={() => {
                setShowApproveInput(false);
                setApproveReason("");
              }}
              color="default"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApprove}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} />
                ) : (
                  <CheckCircleIcon />
                )
              }
            >
              Confirmar aprobación
            </Button>
          </>
        )}

        {showRejectInput && (
          <>
            <Button
              onClick={() => {
                setShowRejectInput(false);
                setRejectReason("");
              }}
              color="default"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReject}
              color="secondary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={16} /> : <CancelIcon />
              }
            >
              Confirmar rechazo
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ReservationDetail;
